import React from 'react';
import { Box, Typography, Button, LinearProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TOUR_THEME } from './tourTheme';
import { TourTooltipArrow } from './TourTooltipArrow';

const btnBase = {
  fontFamily: TOUR_THEME.fontFamily,
  fontSize: '12px',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '8px',
  minHeight: 32,
  px: 1.5,
};

export function TourTooltip({
  continuous,
  index,
  step,
  size,
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
}) {
  return (
    <Box
      {...tooltipProps}
      sx={{
        position: 'relative',
        overflow: 'visible',
        fontFamily: TOUR_THEME.fontFamily,
        bgcolor: TOUR_THEME.panelBg,
        borderRadius: `${TOUR_THEME.borderRadius}px`,
        boxShadow: TOUR_THEME.panelShadow,
        border: `1px solid ${TOUR_THEME.panelBorder}`,
        p: '14px 16px 12px',
        maxWidth: TOUR_THEME.tooltipMaxWidth,
        color: TOUR_THEME.textPrimary,
      }}
    >
      <TourTooltipArrow placement={step.placement} />
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Typography
          component="span"
          sx={{
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: TOUR_THEME.textCaption,
          }}
        >
          Paso {index + 1} de {size}
        </Typography>
        <IconButton
          {...skipProps}
          size="small"
          aria-label="Saltar tour"
          sx={{
            mt: -0.75,
            mr: -0.75,
            color: TOUR_THEME.textMuted,
            '&:hover': { color: TOUR_THEME.textSecondary, bgcolor: 'rgba(255,255,255,0.06)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {step.title && (
        <Typography
          component="h3"
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: 1.35,
            color: TOUR_THEME.textPrimary,
            mb: 0.75,
          }}
        >
          {step.title}
        </Typography>
      )}

      <Typography
        sx={{
          fontSize: '12.5px',
          lineHeight: 1.45,
          color: TOUR_THEME.textSecondary,
          mb: 1.75,
        }}
      >
        {step.content}
      </Typography>

      <LinearProgress
        variant="determinate"
        value={((index + 1) / size) * 100}
        sx={{
          mb: 1.75,
          height: 3,
          borderRadius: 99,
          bgcolor: 'rgba(255,255,255,0.08)',
          '& .MuiLinearProgress-bar': {
            borderRadius: 99,
            bgcolor: TOUR_THEME.accent,
          },
        }}
      />

      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Button
          {...skipProps}
          size="small"
          sx={{
            ...btnBase,
            color: TOUR_THEME.textMuted,
            minWidth: 'auto',
            px: 0.5,
            '&:hover': { color: TOUR_THEME.textSecondary, bgcolor: 'transparent' },
          }}
        >
          Saltar tour
        </Button>
        <Box display="flex" gap={0.75}>
          {index > 0 && (
            <Button
              {...backProps}
              size="small"
              variant="outlined"
              sx={{
                ...btnBase,
                color: TOUR_THEME.textSecondary,
                borderColor: 'rgba(255,255,255,0.14)',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.28)',
                  bgcolor: 'rgba(255,255,255,0.04)',
                },
              }}
            >
              Atrás
            </Button>
          )}
          <Button
            {...primaryProps}
            size="small"
            variant="contained"
            disableElevation
            sx={{
              ...btnBase,
              bgcolor: TOUR_THEME.accent,
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: TOUR_THEME.accentHover },
            }}
          >
            {continuous && index < size - 1 ? 'Siguiente' : 'Finalizar'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
